/**
 * Maps country codes (ISO 3166-1 alpha-2) to their corresponding locale strings
 * for use with Intl.NumberFormat and other internationalization APIs.
 *
 * The locale format follows BCP 47 language tags: language-REGION
 * For countries with multiple official languages, we choose the most common or official language.
 */
const countryCodeToLocale: Record<string, string> = {
  // Americas
  US: 'en-US', // United States
  CA: 'en-CA', // Canada (English is more common than French in most regions)
  MX: 'es-MX', // Mexico
  BR: 'pt-BR', // Brazil
  AR: 'es-AR', // Argentina
  CL: 'es-CL', // Chile
  CO: 'es-CO', // Colombia
  PE: 'es-PE', // Peru
  VE: 'es-VE', // Venezuela

  // Europe
  GB: 'en-GB', // United Kingdom
  IE: 'en-IE', // Ireland
  DE: 'de-DE', // Germany
  FR: 'fr-FR', // France
  ES: 'es-ES', // Spain
  IT: 'it-IT', // Italy
  PT: 'pt-PT', // Portugal
  NL: 'nl-NL', // Netherlands
  BE: 'nl-BE', // Belgium (Dutch is more widely spoken than French)
  AT: 'de-AT', // Austria
  CH: 'de-CH', // Switzerland (German is most common, but also fr-CH, it-CH)
  SE: 'sv-SE', // Sweden
  NO: 'nb-NO', // Norway (Bokmål is more common than Nynorsk)
  DK: 'da-DK', // Denmark
  FI: 'fi-FI', // Finland
  PL: 'pl-PL', // Poland
  CZ: 'cs-CZ', // Czech Republic
  RU: 'ru-RU', // Russia
  UA: 'uk-UA', // Ukraine
  GR: 'el-GR', // Greece
  TR: 'tr-TR', // Turkey
  RO: 'ro-RO', // Romania
  HU: 'hu-HU', // Hungary
  SK: 'sk-SK', // Slovakia
  BG: 'bg-BG', // Bulgaria
  HR: 'hr-HR', // Croatia

  // Asia
  CN: 'zh-CN', // China (Simplified Chinese)
  JP: 'ja-JP', // Japan
  KR: 'ko-KR', // South Korea
  IN: 'en-IN', // India (English is widely used for business)
  SG: 'en-SG', // Singapore (English is the primary business language)
  HK: 'zh-HK', // Hong Kong (Traditional Chinese)
  TW: 'zh-TW', // Taiwan (Traditional Chinese)
  TH: 'th-TH', // Thailand
  VN: 'vi-VN', // Vietnam
  ID: 'id-ID', // Indonesia
  MY: 'ms-MY', // Malaysia
  PH: 'en-PH', // Philippines (English is widely used)
  PK: 'ur-PK', // Pakistan
  BD: 'bn-BD', // Bangladesh
  IL: 'he-IL', // Israel

  // Middle East & Africa
  AE: 'ar-AE', // United Arab Emirates
  SA: 'ar-SA', // Saudi Arabia
  EG: 'ar-EG', // Egypt
  ZA: 'en-ZA', // South Africa (English is the primary business language)
  NG: 'en-NG', // Nigeria
  KE: 'en-KE', // Kenya

  // Oceania
  AU: 'en-AU', // Australia
  NZ: 'en-NZ', // New Zealand
}

/**
 * Determines the appropriate locale for formatting based on the user's country code and currency.
 *
 * Priority:
 * 1. Use country code to determine locale (most accurate for regional formatting)
 * 2. Fall back to currency-based locale if country code is not available
 * 3. Ultimate fallback: 'en-US'
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB', 'DE')
 * @param currency - ISO 4217 currency code (e.g., 'USD', 'EUR', 'GBP')
 * @returns BCP 47 locale string (e.g., 'en-US', 'de-DE', 'ja-JP')
 */
export const getLocaleFromCountryAndCurrency = (countryCode?: string | null, currency?: string): string => {
  // Priority 1: Use country code to get locale
  if (countryCode) {
    const upperCountryCode = countryCode.toUpperCase()
    if (upperCountryCode in countryCodeToLocale) {
      return countryCodeToLocale[upperCountryCode]
    }
  }

  // Priority 2: Fall back to currency-based locale for edge cases
  // This handles scenarios where we have currency but not country code
  if (currency) {
    const upperCurrency = currency.toUpperCase()
    // For currencies, we construct a locale using common language for that currency
    // This is less accurate than country code but better than nothing
    const currencyFallbacks: Record<string, string> = {
      USD: 'en-US',
      GBP: 'en-GB',
      EUR: 'de-DE', // Default to German for EUR
      INR: 'en-IN',
      CAD: 'en-CA',
      CNY: 'zh-CN',
      AUD: 'en-AU',
      PLN: 'pl-PL',
      BRL: 'pt-BR',
      ILS: 'he-IL',
      TRY: 'tr-TR',
      JPY: 'ja-JP',
      CHF: 'de-CH',
      MXN: 'es-MX',
      AED: 'ar-AE',
      SEK: 'sv-SE',
      SGD: 'en-SG',
      ZAR: 'en-ZA',
      NZD: 'en-NZ',
      THB: 'th-TH',
      TWD: 'zh-TW',
      CZK: 'cs-CZ',
      KRW: 'ko-KR',
    }

    if (upperCurrency in currencyFallbacks) {
      return currencyFallbacks[upperCurrency]
    }
  }

  // Priority 3: Ultimate fallback
  return 'en-US'
}
